import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./accountSync.module.css";
import { getAccountSyncIntent, clearAccountSyncIntent, getAccountSyncBatch, confirmAccountSync } from "../../api/client";

const OPERATOR = "cong.li@cue.group";
const STATES = {
  pending: "待确认", running: "正在同步", complete: "同步完成", stopped: "同步已中止",
  stale: "清单已变化", blocked: "需要人工核对", resolved: "差异已消除",
};
const REASONS = {
  target_has_independent_or_edited_transactions: "目标存在独有或被修改的交易",
  shared_transaction_order_differs: "两边交易基线或顺序不同",
  source_row_is_revision_not_new_trade: "来源交易曾被更正",
  ambiguous_duplicate_trade: "存在无法区分的相同交易",
  missing_or_reversed_source_audit: "来源操作证据不完整或已撤销",
  opening_position_differs: "录入持仓不同",
  opening_position_requires_manual_review: "含录入持仓，需要人工核对",
  new_holding_requires_manual_review: "新增标的含录入持仓或状态需要核对",
  holding_not_active: "持仓不是在持状态",
  target_holding_missing: "目标尚无此标的",
  target_has_other_holding_lifecycle: "目标已有其他生命周期的持仓",
  target_only_holding: "仅目标拥有此标的",
  source_price_confirmation_requires_manual_review: "来源交易含异常价确认，需要人工核对",
  source_columns_and_facts_disagree: "来源事实字段不一致",
  source_asset_identity_disagrees: "来源标的身份不一致",
  asset_type_differs: "标的类型不同", unsupported_action: "交易类型需要人工处理",
};

export function AccountSyncBoundary({ session, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isReview = location.pathname.startsWith("/account-sync/");
  const operator = session?.username?.toLowerCase() === OPERATOR;
  const [checking, setChecking] = React.useState(operator && !isReview);
  React.useEffect(() => {
    let active = true;
    if (!operator || isReview) { setChecking(false); return undefined; }
    getAccountSyncIntent()
      .then((data) => {
        if (!active) return;
        if (data.batch_id) navigate(`/account-sync/${data.batch_id}`, { replace: true });
        else setChecking(false);
      })
      .catch(() => { if (active) setChecking(false); }); // 未启用时保持原应用入口。
    return () => { active = false; };
  }, [operator, isReview, navigate]);
  if (isReview) return <AccountSync batchId={location.pathname.split("/")[2]} allowed={operator} onBack={() => navigate("/overview")} />;
  if (checking) return <main className={styles.page} role="status">正在恢复确认清单…</main>;
  return children;
}

function AccountSync({ batchId, allowed, onBack }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    let timer;
    if (!allowed) return undefined;
    async function load() {
      try {
        const next = await getAccountSyncBatch(batchId);
        if (!active) return;
        setData(next);
        if (next.state === "running") timer = setTimeout(load, 2000);
        // 清掉已恢复的意图，之后普通打开应用不再反复跳回维护页。
        void clearAccountSyncIntent().catch(() => {});
      } catch (e) { if (active) setError(e.payload?.detail?.message || e.message); }
    }
    void load();
    return () => { active = false; clearTimeout(timer); };
  }, [batchId, allowed, refresh]);

  async function confirm() {
    if (submitting || data?.state !== "pending") return;
    setSubmitting(true);
    setError("");
    try {
      const next = await confirmAccountSync(batchId, data.confirmation_token);
      setData(next);
    } catch (e) {
      setError(`${e.payload?.detail?.message || e.message} 如请求中断，请查看执行状态，不要重复提交。`);
    } finally { setSubmitting(false); setRefresh((n) => n + 1); }
  }

  return <main className={styles.page} id="main-content">
    <section className={styles.panel} aria-labelledby="sync-title">
      <header className={styles.header}>
        <div><p className={styles.eyebrow}>账号维护</p><h1 id="sync-title">交易同步</h1></div>
        <button type="button" onClick={onBack}>返回应用</button>
      </header>
      {!allowed ? <p role="alert">仅 cong.li 登录后可查看和确认交易同步，请在 Lucimix 切换到授权账号后重新打开消息链接。</p>
        : <>
          {error && <p className={styles.error} role="alert">{error}</p>}
          {!data && !error && <p role="status">正在读取待补交易…</p>}
          {data && <>
            <p className={styles.direction}>{data.source} <span aria-label="同步至">→</span> {data.target}</p>
            <div className={styles.summary}><strong>{STATES[data.state] || "状态待核验"}</strong>
              <span>发现于 {new Date(data.created_at).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false })}（北京时间）</span></div>
            <h2>{data.state === "complete" ? "已补交易" : "待补交易"} · {data.transactions.length} 笔</h2>
            <div className={styles.tableWrap}><table><thead><tr>
              <th>标的</th><th>交易日期</th><th>方向</th><th>数量</th><th>成交价</th><th>实际费用</th>
            </tr></thead><tbody>{data.transactions.map((row) => <tr key={row.id}>
              <td><strong>{row.asset_name || row.asset_code}</strong><small>{row.asset_code}{row.new_holding ? " · 新增标的" : ""}</small></td>
              <td>{row.trade_date}</td><td>{row.action === "BUY" ? "买入" : "卖出"}</td>
              <td>{row.quantity}</td><td>{row.price}</td><td>{row.actual_fee ?? row.fee ?? "按现有费用规则"}</td>
            </tr>)}</tbody></table></div>
            {data.conflicts.length > 0 && <section className={styles.conflicts}>
              <h2>需人工核对 · {data.conflicts.length} 项</h2>
              <p>以下差异不会自动同步。</p>
              {data.conflicts.map((conflict, index) => <div key={`${conflict.asset_code}-${index}`}>
                <h3>{conflict.asset_code}</h3><p>{conflict.reasons.map((reason) => REASONS[reason] || "数据差异需要人工核对").join("；")}</p>
                {(conflict.source_only || []).map((trade, i) => <p key={i}>来源独有：{trade[0]} · {trade[1] === "BUY" ? "买入" : "卖出"} · 数量 {trade[2]} · 价格 {trade[3]} · 费用 {trade[4] ?? "未提供"}</p>)}
              </div>)}
            </section>}
            {data.result?.message && <section className={styles.result} role="status">
              <h2>{STATES[data.state]}</h2><p>{data.result.message}</p>
              {(data.result.receipts || []).map((receipt, i) => <p key={i}>{receipt.asset_code}：{
                receipt.state === "not_submitted" ? "未执行" : receipt.http_status === 200 ? receipt.calculation_status === "success" ? "已保存，重算成功" : "已保存，核算未成功"
                  : [401, 403, 409, 422].includes(receipt.http_status) ? "提交未通过" : "结果待核验"
              }</p>)}
            </section>}
            {data.state === "pending" && <footer className={styles.actions}>
              <p>确认后仅补入上述可同步交易，并重新核算。</p>
              <button className={styles.confirm} type="button" disabled={submitting} onClick={confirm}>{submitting ? "正在提交…" : "确认同步"}</button>
            </footer>}
            {data.state === "running" && <p role="status">正在补录和重算，请稍候。此页面会自动更新执行结果。</p>}
            {data.state === "stale" && <p role="status">来源或目标已变化，本清单不可再确认。请打开最新提醒核对。</p>}
          </>}
        </>}
    </section>
  </main>;
}
